/**
 * CategoryForm
 * Create/edit form for a Help category. Simpler sibling of ArticleForm: Name
 * and Sort Order only.
 */
import React, { useEffect } from 'react';
import { useForm, type UseFormSetError } from 'react-hook-form';
import { Input } from '@components/common';
import type { HelpCategory, CreateHelpCategoryPayload, UpdateHelpCategoryPayload } from '@app-types';
import styles from './HelpForm.module.css';

export interface CategoryFormValues {
  name: string;
  sortOrder: number;
}

export interface CategoryFormProps {
  category?: HelpCategory;
  defaultSortOrder: number;
  onSubmit: (data: CreateHelpCategoryPayload | UpdateHelpCategoryPayload) => void;
  formRef?: React.Ref<HTMLFormElement>;
  /**
   * Receives the form's setError so the parent can bind server-side failures
   * (e.g. a 409 duplicate name) to the offending field instead of only
   * surfacing them as a toast.
   */
  onSetErrorReady?: (setError: UseFormSetError<CategoryFormValues>) => void;
}

const NAME_MAX_LENGTH = 100;

const CategoryForm: React.FC<CategoryFormProps> = ({
  category,
  defaultSortOrder,
  onSubmit,
  formRef,
  onSetErrorReady
}) => {
  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors }
  } = useForm<CategoryFormValues>({
    mode: 'onChange',
    defaultValues: {
      name: category?.name ?? '',
      sortOrder: category?.sort_order ?? defaultSortOrder
    }
  });

  useEffect(() => {
    reset({
      name: category?.name ?? '',
      sortOrder: category?.sort_order ?? defaultSortOrder
    });
  }, [category, defaultSortOrder, reset]);

  useEffect(() => {
    onSetErrorReady?.(setError);
  }, [onSetErrorReady, setError]);

  const submitHandler = handleSubmit((data) => {
    onSubmit({
      name: data.name.trim(),
      sortOrder: data.sortOrder
    });
  });

  return (
    <form ref={formRef} onSubmit={submitHandler} className={styles.form} data-test-id="category-form">
      <Input
        label="Name"
        required
        error={errors.name?.message}
        dataTestId="category-form-name"
        {...register('name', {
          required: 'Name is required',
          maxLength: {
            value: NAME_MAX_LENGTH,
            message: `Name must be at most ${NAME_MAX_LENGTH} characters`
          }
        })}
      />

      <Input
        type="number"
        label="Sort Order"
        required
        error={errors.sortOrder?.message}
        dataTestId="category-form-sort-order"
        {...register('sortOrder', {
          valueAsNumber: true,
          validate: {
            required: (v) => (v !== undefined && v !== null && !Number.isNaN(v)) || 'Sort order is required',
            integer: (v) => Number.isNaN(v) || Number.isInteger(v) || 'Must be a whole number'
          }
        })}
      />
    </form>
  );
};

export default CategoryForm;
